import type {
  LocalTrocaPlantao,
  TrocaPlantaoHistorico,
  TrocaPlantaoSubstituto,
} from "@/services/Sync";

/**
 * A troca de plantão só existe para escala 12x36 ou 24x72. A fonte é o `tipo_escala`
 * do cadastro da jornada (tab_adm_cadastros_jornadas: 'S' semanal, 'D' 12x36, 'V' 24x72,
 * 'C' ciclo — mesma legenda do TirvuAdm). Jornada sem o campo (/iniciar antigo) cai no
 * `is1236`/`is2472` = 'S'. Olha TODAS as jornadas do colaborador (o array do /iniciar
 * pode ter mais de uma) e os dois lugares em que ele aparece no userData.
 */
export const ehEscalaDePlantao = (userData: any): boolean => {
  const jornadas = [
    ...(Array.isArray(userData?.jornada) ? userData.jornada : []),
    ...(Array.isArray(userData?.colaborador?.jornada)
      ? userData.colaborador.jornada
      : []),
  ];
  const valor = (v: unknown) =>
    String(v ?? "")
      .trim()
      .toUpperCase();
  return jornadas.some((j: any) => {
    const escala = valor(j?.tipo_escala);
    if (escala) return escala === "D" || escala === "V";
    return valor(j?.is1236) === "S" || valor(j?.is2472) === "S";
  });
};

/**
 * Interruptor "Troca de Plantão" do cadastro do POSTO (TirvuAdm, 28/10/2026), que o /iniciar do
 * t-credentials entrega como `permite_troca_plantao`. A regra de todo `permite_*` do app (ver
 * `lerFlag` em components/solicitacao/comum): só o 'N' explícito bloqueia; ausente ou vazio libera
 * — servidor antigo ou base sem a coluna não tira a troca de quem já usa.
 */
export const permiteTrocaPlantao = (userData: any): boolean => {
  for (const fonte of [userData, userData?.done, userData?.colaborador]) {
    const v = fonte?.permite_troca_plantao;
    if (v != null && String(v).trim() !== "")
      return String(v).trim().toUpperCase() !== "N";
  }
  return true;
};

/** A troca aparece no app: escala 12x36/24x72 E o posto não desligou a troca. */
export const podeUsarTrocaPlantao = (userData: any): boolean =>
  ehEscalaDePlantao(userData) && permiteTrocaPlantao(userData);

// Partículas não contam como sobrenome: "MARIA DA SILVA" → S, não D.
const PARTICULAS = new Set(["DA", "DAS", "DE", "DO", "DOS", "E"]);

/** 1ª letra do sobrenome ("" quando o nome tem uma palavra só). */
export const inicialDoSobrenome = (nomeCompleto: string): string => {
  const sobrenomes = String(nomeCompleto ?? "")
    .trim()
    .split(/\s+/)
    .slice(1)
    .filter((p) => !PARTICULAS.has(p.toUpperCase()));
  return (sobrenomes[0] ?? "").charAt(0).toUpperCase();
};

/**
 * Rótulo LGPD do substituto: matrícula + PRIMEIRO nome + inicial do sobrenome mascarada
 * ("1234 - JOSÉ S*****"). O nome completo de um colega não é necessário para escolher
 * quem cobre o plantão.
 */
export const rotuloSubstituto = (c: TrocaPlantaoSubstituto): string => {
  const primeiro = String(c.primeiro_nome ?? "").trim().toUpperCase();
  const inicial = String(c.inicial_sobrenome ?? "").trim().charAt(0).toUpperCase();
  const nome = inicial ? `${primeiro} ${inicial}*****` : `${primeiro} *****`;
  const matricula = String(c.matricula ?? "").trim();
  return matricula ? `${matricula} - ${nome}` : nome;
};

/** "1234 - JOSÉ S*****" → "JOSÉ S." (o rótulo curto que cabe na coluna do histórico). */
export const colegaCurto = (rotulo: string): string => {
  const semMatricula = String(rotulo ?? "").replace(/^.*? - /, "").trim();
  const [primeiro = "", resto = ""] = semMatricula.split(/\s+/, 2);
  const inicial = resto.replace(/[^A-Za-zÀ-ÿ]/g, "").charAt(0);
  return inicial ? `${primeiro} ${inicial}.` : primeiro;
};

/** "DD/MM/YYYY" → "YYYY-MM-DD" ("" se não for uma data nesse formato). */
export const dataBRparaISO = (br: string): string => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(br ?? "").trim());
  return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
};

/** "YYYY-MM-DD" → "DD/MM/YYYY" ("" se não for uma data nesse formato). */
export const dataISOparaBR = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? "").trim());
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

export type TrocaDoDiaTipo =
  | "aguardando_envio"
  /** Eu pedi e o colega ainda não respondeu. */
  | "aguardando_colega"
  /** Pediram para eu cobrir e eu ainda não respondi. */
  | "aguardando_voce"
  /** Os dois lados acertaram; falta o supervisor. Cor própria (azul) na tela Troca de Plantão. */
  | "aguardando_admin"
  | "aceita"
  | "recusada_colega"
  | "recusada_admin";

type Status = {
  tipo: TrocaDoDiaTipo;
  /** "TROCA PENDENTE" | "AGUARDANDO SUPERVISOR" | "TROCA ACEITA" | "TROCA RECUSADA". */
  selo: string;
  /** Etapa, curta ("Aguardando colega", "Colega aceitou", "Aceita pelo supervisor"…). */
  detalhe: string;
};

/**
 * Status em duas etapas: primeiro o colega (`aceito_substituto`), depois o supervisor
 * (`aceito`). O texto muda conforme o LADO de quem olha (pedi / cubro).
 *
 * ⚠️ Quem decide aparece SEMPRE como "supervisor", nunca pelo nome: o nome de quem aprova no
 * TirvuAdm não pode sair no app (27/10/2026). O servidor também deixou de mandá-lo.
 */
export const statusDaTroca = (h: TrocaPlantaoHistorico): Status => {
  const cubro = h.papel === "C";
  if (h.aceito === "N")
    return {
      tipo: "recusada_admin",
      selo: "TROCA RECUSADA",
      detalhe: "Recusada pelo supervisor",
    };
  if (h.aceito_substituto === "N")
    return {
      tipo: "recusada_colega",
      selo: "TROCA RECUSADA",
      detalhe: cubro ? "Você recusou" : "Recusada pelo colega",
    };
  if (h.aceito === "S")
    return {
      tipo: "aceita",
      selo: "TROCA ACEITA",
      detalhe: "Aceita pelo supervisor",
    };
  if (h.aceito_substituto === "S")
    return {
      tipo: "aguardando_admin",
      selo: "AGUARDANDO SUPERVISOR",
      detalhe: cubro ? "Você aceitou" : "Colega aceitou",
    };
  return cubro
    ? {
        tipo: "aguardando_voce",
        selo: "TROCA PENDENTE",
        detalhe: "Aguardando sua resposta",
      }
    : {
        tipo: "aguardando_colega",
        selo: "TROCA PENDENTE",
        detalhe: "Aguardando colega",
      };
};

/** Uma troca na tela "Troca de Plantão" — um card por troca, com os dois dias dentro. */
export interface TrocaDaLista {
  /** Id do servidor, ou "fila:YYYY-MM-DD" para o pedido que só existe neste aparelho. */
  chave: string;
  id: number;
  tipo: TrocaDoDiaTipo;
  selo: string;
  detalhe: string;
  /** 'S' = eu pedi · 'C' = eu cubro o plantão do colega. */
  papel: "S" | "C";
  colega: string;
  dataTroca: string; // "YYYY-MM-DD"
  dataPagamento: string; // "YYYY-MM-DD"
  /** "YYYY-MM-DD HH:mm:ss" ("" quando o servidor não mandou). */
  pedidoEm: string;
  /** Quando o colega aceitou ou recusou. */
  respondidoEm: string;
  decididoEm: string;
  obsColega: string;
  obsSupervisor: string;
  aceitoColega: string;
  aceitoSupervisor: string;
  offline: boolean;
}

/**
 * As trocas do período para a lista: as do servidor (os dois lados) e os pedidos deste aparelho que
 * o servidor ainda não devolveu — na fila ("Aguardando envio") ou recém-enviados. Mais recentes
 * primeiro, pela data da troca.
 */
export const trocasDaLista = (
  locais: Record<string, LocalTrocaPlantao>,
  historico: TrocaPlantaoHistorico[],
  inicio: string,
  fim: string,
): TrocaDaLista[] => {
  const noPeriodo = (iso: string) => !!iso && iso >= inicio && iso <= fim;
  const lista = new Map<string, TrocaDaLista>();

  for (const h of historico) {
    if (!noPeriodo(h.data_troca) && !noPeriodo(h.data_pagamento)) continue;
    const chave = String(h.id_rh_troca_plantao_app);
    if (lista.has(chave)) continue;
    lista.set(chave, {
      ...statusDaTroca(h),
      chave,
      id: h.id_rh_troca_plantao_app,
      papel: h.papel,
      colega: h.colega || "colega",
      dataTroca: h.data_troca,
      dataPagamento: h.data_pagamento,
      pedidoEm: h.data_log ?? "",
      respondidoEm: h.data_log_substituto ?? "",
      decididoEm: h.data_log_validacao ?? "",
      obsColega: h.observacao_substituto ?? "",
      obsSupervisor: h.observacao_validacao ?? "",
      aceitoColega: h.aceito_substituto,
      aceitoSupervisor: h.aceito,
      offline: false,
    });
  }

  for (const [iso, local] of Object.entries(locais)) {
    if (!noPeriodo(iso) && !noPeriodo(local.data_pagamento)) continue;
    // Já enviado e já devolvido pelo servidor: vale a linha do servidor (status de verdade).
    const doServidor = historico.some(
      (h) => h.papel === "S" && h.data_troca === iso,
    );
    if (!local.offline && doServidor) continue;
    const pedidoEm = local.timestamp
      ? dataHoraLocal(new Date(local.timestamp))
      : "";
    lista.set(`fila:${iso}`, {
      chave: `fila:${iso}`,
      id: 0,
      tipo: local.offline ? "aguardando_envio" : "aguardando_colega",
      selo: "TROCA PENDENTE",
      detalhe: local.offline ? "Aguardando envio" : "Aguardando colega",
      papel: "S",
      colega: colegaCurto(local.substituto) || "colega",
      dataTroca: iso,
      dataPagamento: local.data_pagamento,
      pedidoEm,
      respondidoEm: "",
      decididoEm: "",
      obsColega: "",
      obsSupervisor: "",
      aceitoColega: "P",
      aceitoSupervisor: "P",
      offline: local.offline,
    });
  }

  return [...lista.values()].sort((a, b) =>
    b.dataTroca.localeCompare(a.dataTroca),
  );
};

const dataHoraLocal = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

export type EtapaEstado = "feito" | "recusado" | "aguardando" | "inativo";

/** Uma etapa da linha do tempo da troca: pedido → colega → supervisor. */
export interface EtapaDaTroca {
  titulo: string;
  estado: EtapaEstado;
  /** "28/10 às 14:32" ou um texto curto ("Aguardando…"). */
  quando: string;
  /** Observação de quem recusou (colega ou supervisor), quando houver. */
  observacao: string;
}

/** "YYYY-MM-DD HH:mm:ss" → "28/10 às 14:32" ("" se não der). */
export const quandoCurto = (dataHora: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(
    String(dataHora ?? "").trim(),
  );
  return m ? `${m[3]}/${m[2]} às ${m[4]}:${m[5]}` : "";
};

/**
 * As 3 etapas da troca, do ponto de vista de quem olha, cada uma com a hora em que aconteceu (a do
 * colega é a do toque no pop-up, guardada pela fila). Quem decide é SEMPRE "supervisor" (nunca o
 * nome — ver statusDaTroca). T-ARC antigo, sem `data_log_substituto`: a etapa sai sem hora.
 */
export const etapasDaTroca = (t: TrocaDaLista): EtapaDaTroca[] => {
  const cubro = t.papel === "C";
  const pedido: EtapaDaTroca = {
    titulo: t.offline
      ? "Pedido salvo no aparelho"
      : cubro
        ? `${t.colega} pediu a troca`
        : "Você pediu a troca",
    estado: t.offline ? "aguardando" : "feito",
    quando: t.offline
      ? "Sobe sozinho quando houver internet"
      : quandoCurto(t.pedidoEm),
    observacao: "",
  };

  const colega: EtapaDaTroca =
    t.aceitoColega === "S"
      ? {
          titulo: cubro ? "Você aceitou e assinou" : "Colega aceitou e assinou",
          estado: "feito",
          quando: quandoCurto(t.respondidoEm),
          observacao: "",
        }
      : t.aceitoColega === "N"
        ? {
            titulo: cubro ? "Você recusou" : "Colega recusou",
            estado: "recusado",
            quando: quandoCurto(t.respondidoEm),
            observacao: t.obsColega,
          }
        : {
            titulo: cubro ? "Sua resposta" : "Resposta do colega",
            estado: t.offline ? "inativo" : "aguardando",
            quando: cubro ? "Aguardando você aceitar ou recusar" : "Aguardando",
            observacao: "",
          };

  const supervisor: EtapaDaTroca =
    t.aceitoSupervisor === "S"
      ? {
          titulo: "Aceita pelo supervisor",
          estado: "feito",
          quando: quandoCurto(t.decididoEm),
          observacao: "",
        }
      : t.aceitoSupervisor === "N"
        ? {
            titulo: "Recusada pelo supervisor",
            estado: "recusado",
            quando: quandoCurto(t.decididoEm),
            observacao: t.obsSupervisor,
          }
        : t.aceitoColega === "N"
          ? {
              titulo: "Supervisor",
              estado: "inativo",
              quando: "Não segue: a troca foi recusada",
              observacao: "",
            }
          : {
              titulo: "Supervisor",
              estado: t.aceitoColega === "S" ? "aguardando" : "inativo",
              quando:
                t.aceitoColega === "S"
                  ? "Aguardando a análise"
                  : "Depois que o colega aceitar",
              observacao: "",
            };

  return [pedido, colega, supervisor];
};
